import React from "react";
import { useTranslation } from "react-i18next";
import { getIcon } from "../api/utils/iconMapping";

/**
 * Stats Component
 * @param {Object} props
 * @param {Array} props.data - Array of stats data from Directus
 * @param {string} props.gradient - CSS gradient classes for the icon background
 * @param {string} props.gridCols - Grid column classes (e.g., "grid-cols-2 md:grid-cols-3")
 * @param {boolean} props.isLoading - Loading state
 * @param {Error} props.error - Error state
 * @param {string} props.language - Current language code (e.g., "en-US", "sk-SK")
 */
const Stats = ({
  data = [],
  gradient = "from-teal to-sage",
  gridCols = "grid-cols-2 md:grid-cols-4",
  isLoading = false,
  error = null,
  language = "en-US",
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">{t("common.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">
          {t("common.error")}: {error.message}
        </p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No stats available.</p>
      </div>
    );
  }

  return (
    <div className={`grid ${gridCols} gap-8`}>
      {data.map((stat) => {
        const IconComponent = getIcon(stat.icon);

        // Get translated text for the current language
        // Try multiple matching strategies for better language support
        const currentLang = language || "en-US";
        const langPrefix = currentLang.split("-")[0]; // e.g., "en" from "en-US"

        const exactMatch = stat.translations?.find(
          (t) => t.languages_code === currentLang
        );
        const prefixMatch = stat.translations?.find((t) =>
          t.languages_code?.startsWith(langPrefix)
        );
        const fallbackMatch = stat.translations?.find(
          (t) => t.languages_code === "en-US"
        );

        const translation = exactMatch || prefixMatch || fallbackMatch;
        const label = translation?.name || stat.value || "Stat";

        // Debug logging (remove this after testing)
        console.log(`Stat ${stat.id}:`, {
          currentLang,
          langPrefix,
          availableTranslations: stat.translations?.map(
            (t) => t.languages_code
          ),
          selectedTranslation: translation?.languages_code,
          label,
        });

        return (
          <div key={stat.id} className="text-center">
            <div className="flex justify-center mb-4">
              <div
                className={`flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r ${gradient}`}
              >
                <IconComponent className="h-8 w-8 text-white" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">
              {stat.value}
            </h3>
            <p className="text-gray-600">{label}</p>
          </div>
        );
      })}
    </div>
  );
};

export default Stats;
